import React from 'react';

import {useStore} from 'effector-react';

import {useTranslation} from 'react-i18next';

import {ReactComponent as CheckIcon} from '../../../assets/svg/check.svg';
import {ReactComponent as CrossIcon} from '../../../assets/svg/cross.svg';
import {I18N_NS} from '../../../core/constants/names';
import {DocumentStatus} from '../../../core/dictionaries';
import {
    $allowedActionsForInvoice,
    $allowToCorrectInvoice,
    $invoiceStatusInfo,
    AllowedActionsForInvoice,
    fetchInvoiceStatusAcceptedFx,
    fetchInvoiceStatusRejectedFx,
    onAnnullateInvoice,
    setAllowToCorrectInvoice,
} from '../../../models/common';
import {$invoiceCreatingPending} from '../../../models/details';
import {Button, ButtonVariant} from '../../ui-kit/Button';
import {Icon} from '../../ui-kit/Icon';

import {StatusButtonsWrapper} from './styled';

const CustomerButtons = ({
    changeInvoiceStatus,
    allowedActionsForInvoice,
    invoiceCreatingPending,
    t,
}: {
    changeInvoiceStatus: (to: DocumentStatus) => void;
    allowedActionsForInvoice: AllowedActionsForInvoice;
    invoiceCreatingPending: boolean;
    t: (key: string) => string;
}) => (
    <>
        {allowedActionsForInvoice.reject && (
            <Button
                disabled={invoiceCreatingPending}
                style={{whiteSpace: 'nowrap'}}
                variant={ButtonVariant.Cancel}
                size="sm"
                type="button"
                onClick={() => {
                    changeInvoiceStatus(DocumentStatus['REJECTED']);
                }}
            >
                {t('REJECT')}
                <Icon style={{marginLeft: 6}} source={CrossIcon} width={10} height={10} />
            </Button>
        )}
        {allowedActionsForInvoice.accept && (
            <Button
                disabled={invoiceCreatingPending}
                style={{whiteSpace: 'nowrap'}}
                variant={ButtonVariant.Normal}
                size="sm"
                type="button"
                onClick={() => {
                    changeInvoiceStatus(DocumentStatus['ACCEPTED']);
                }}
            >
                {t('ACCEPT')}
                <Icon style={{marginLeft: 6}} source={CheckIcon} width={16} height={16} />
            </Button>
        )}
    </>
);

const SupplierButtons = ({
    allowedActionsForInvoice,
    invoiceCreatingPending,
    t,
}: {
    allowedActionsForInvoice: AllowedActionsForInvoice;
    invoiceCreatingPending: boolean;
    t: (key: string) => string;
}) => (
    <>
        {!$allowToCorrectInvoice.getState() && (
            <>
                {allowedActionsForInvoice.cancel && (
                    <Button
                        style={{whiteSpace: 'nowrap'}}
                        disabled={invoiceCreatingPending}
                        size="sm"
                        variant={ButtonVariant.Cancel}
                        onClick={() => {
                            onAnnullateInvoice();
                        }}
                        type="button"
                    >
                        {t('annulate')}
                    </Button>
                )}
                {allowedActionsForInvoice.correct && (
                    <Button
                        variant={ButtonVariant.Normal}
                        disabled={invoiceCreatingPending}
                        size="sm"
                        onClick={() => {
                            setAllowToCorrectInvoice(true);
                        }}
                        type="button"
                    >
                        {t('correct')}
                    </Button>
                )}
            </>
        )}
    </>
);

export const ControlButtons = ({id}: {id: string | undefined}) => {
    const invoiceStatusInfo = useStore($invoiceStatusInfo);
    const allowedActionsForInvoice = useStore($allowedActionsForInvoice);
    const invoiceCreatingPending = useStore($invoiceCreatingPending);
    const {t: translate} = useTranslation(I18N_NS);
    const t = (key: string) => translate('dictionaries.documents.statusInvoice.' + key);

    function changeInvoiceStatus(to: DocumentStatus) {
        if (invoiceStatusInfo && id) {
            if (to === DocumentStatus['REJECTED']) {
                fetchInvoiceStatusRejectedFx(id);
            }

            if (to === DocumentStatus['ACCEPTED']) {
                fetchInvoiceStatusAcceptedFx(id);
            }
        }
    }
    return (
        <StatusButtonsWrapper>
            <SupplierButtons
                allowedActionsForInvoice={allowedActionsForInvoice}
                invoiceCreatingPending={invoiceCreatingPending}
                t={t}
            />
            <CustomerButtons
                changeInvoiceStatus={changeInvoiceStatus}
                allowedActionsForInvoice={allowedActionsForInvoice}
                invoiceCreatingPending={invoiceCreatingPending}
                t={t}
            />
        </StatusButtonsWrapper>
    );
};
